import { useEffect, useState } from "react";
import type { CookingSessionStatus } from "@/types/cooking-session";

/**
 * The cook clock (T-1101).
 *
 * The board showed "0m / 27m" and 0% right through to completion. Two reasons,
 * and both are here:
 *
 *  1. Whole minutes. Floored, a cook reads 0m for its first sixty seconds and
 *     the ring moves in 3.7% steps against a 27-minute target — for most of a
 *     service, indistinguishable from a timer that never started.
 *  2. Nothing ticking. The queries poll every ten seconds and the components
 *     render whatever the last response said, so even at seconds resolution the
 *     number would sit still and then jump.
 *
 * So the client keeps its own clock, anchored on the server's answer and
 * re-anchored on every poll. Anchored on the *elapsed figure*, not on
 * `startedAt`, deliberately: a tablet whose clock is ten minutes off the
 * server's would otherwise show a ten-minute-old cook as twenty.
 */

export interface ElapsedSource {
  status: CookingSessionStatus;
  startedAt: string | null;
  elapsedSeconds: number;
}

/** A cook only accrues time while it is actually cooking. */
export function isRunning(status: CookingSessionStatus): boolean {
  return status === "COOKING";
}

/**
 * A session that finished without ever being started — the orders board's
 * Complete button dismisses tickets no chef picked up, and the API allows
 * ON_DECK → COMPLETED for exactly that. "0 of 27 minutes" is not what happened
 * to those; nothing happened to those.
 */
export function neverStarted(session: ElapsedSource): boolean {
  return session.startedAt === null && (session.status === "COMPLETED" || session.status === "CANCELLED");
}

/** The server's figure, carried forward by however long ago we were told it. */
export function tickedSeconds(
  anchorSeconds: number,
  anchoredAtMs: number,
  nowMs: number,
  running: boolean,
): number {
  if (!running) return anchorSeconds;
  return anchorSeconds + Math.max(0, Math.floor((nowMs - anchoredAtMs) / 1000));
}

/** `7:04`, or `1:07:04` once a cook has been going over an hour. */
export function formatElapsed(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const s = safe % 60;
  const m = Math.floor(safe / 60) % 60;
  const h = Math.floor(safe / 3600);
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Percent of target, from seconds so it creeps rather than stepping. */
export function elapsedPct(seconds: number, targetMinutes: number): number {
  if (targetMinutes <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((seconds / (targetMinutes * 60)) * 100)));
}

/** Wall clock, sampled once a second while `active`. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/**
 * Seconds run, moving every second, corrected on every poll.
 *
 * `anchorSeconds` is the server's figure; `running` says whether to carry it
 * forward. Sessions and whole tickets both go through here.
 */
export function useLiveSeconds(anchorSeconds: number, running: boolean): number {
  const anchor = anchorSeconds;
  const now = useNow(running);

  // Re-anchor whenever a poll brings a different figure; between polls, tick.
  const [seen, setSeen] = useState({ anchor, at: now });
  if (seen.anchor !== anchor) setSeen({ anchor, at: now });
  const from = seen.anchor === anchor ? seen : { anchor, at: now };

  return tickedSeconds(from.anchor, from.at, now, running);
}

/** The clock for one cooking session. */
export function useLiveElapsedSeconds(session: ElapsedSource): number {
  return useLiveSeconds(session.elapsedSeconds, isRunning(session.status));
}

/**
 * The same clock for a whole order ticket, on the orders board.
 *
 * A ticket only accrues time while something on it is actually cooking, so a
 * ticket whose sessions are all paused holds where it was.
 */
export function useTicketElapsed(order: {
  elapsedSeconds: number;
  isActive: boolean;
  isPaused: boolean;
}): number {
  return useLiveSeconds(order.elapsedSeconds, order.isActive && !order.isPaused);
}
